import React from 'react';
import { motion } from 'framer-motion';
import { Clock, MessageCircle, Phone, CreditCard, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { Profile, Membership } from './types';
import { PLAN_PRICING } from './types';
import { PAYMENT_LINKS } from './constants';

interface PendingActivationViewProps {
  profile: Profile;
  membership: Membership;
}

const planLabels: Record<string, string> = {
  bronce: 'Bronce',
  plata: 'Plata',
  oro: 'Oro',
};

const periodLabels: Record<string, string> = {
  mensual: 'Mensual',
  anual: 'Anual',
};

export const PendingActivationView: React.FC<PendingActivationViewProps> = ({
  profile,
  membership,
}) => {
  const amount = PLAN_PRICING[membership.plan][membership.billing_period];
  const paymentLink = PAYMENT_LINKS[membership.plan]?.[membership.billing_period] || '';
  const hasPaymentLink = paymentLink.length > 0;
  
  const handleOnlinePayment = () => {
    if (!hasPaymentLink) return;
    
    // Open payment link in new tab
    window.open(paymentLink, '_blank');
    
    // Send WhatsApp notification to admin
    const adminMessage = `🔔 *PAGO INICIADO*

Cliente: ${profile.nombre}
Teléfono: ${profile.telefono}
Plan: ${planLabels[membership.plan]} ${periodLabels[membership.billing_period]}
Monto: ₡${amount.toLocaleString('es-CR')}

El cliente fue redirigido al link de pago.
Por favor verificar el pago y activar la membresía.`;
    
    // Small delay to ensure payment link opens first
    setTimeout(() => {
      window.open(`https://wa.me/50663217695?text=${encodeURIComponent(adminMessage)}`, '_blank');
    }, 500);
  };

  const handleContactWhatsApp = () => {
    const message = `¡Hola! Soy ${profile.nombre} y quiero activar mi membresía ${planLabels[membership.plan]} (${periodLabels[membership.billing_period]}). ¿Cómo puedo proceder con el pago?`;
    window.open(`https://wa.me/50663217695?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-blue-500/20 via-blue-600/10 to-blue-700/5 backdrop-blur-xl rounded-2xl p-6 border border-blue-500/30 mb-6"
    >
      {/* Header with animated icon */}
      <div className="flex items-start gap-4 mb-6">
        <motion.div
          animate={{ 
            scale: [1, 1.1, 1],
            rotate: [0, 5, -5, 0]
          }}
          transition={{ 
            duration: 2, 
            repeat: Infinity,
            repeatDelay: 2
          }}
          className="p-3 rounded-xl bg-blue-500/20 border border-blue-500/30"
        >
          <Clock className="w-8 h-8 text-blue-400" />
        </motion.div>
        
        <div className="flex-1">
          <h2 className="text-xl font-bold text-white mb-1">
            Tu membresía está pendiente de activación
          </h2>
          <p className="text-white/60">
            Completa tu pago en línea o contáctanos para coordinar.
          </p>
        </div>
      </div>

      {/* Plan details */}
      <div className="bg-white/5 rounded-xl p-4 mb-6 border border-white/10">
        <h3 className="text-sm font-medium text-white/50 mb-3 flex items-center gap-2">
          <CreditCard className="w-4 h-4" />
          Detalles de tu plan
        </h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-white/50 text-xs mb-1">Plan seleccionado</p>
            <p className="text-white font-semibold text-lg">{planLabels[membership.plan]}</p>
          </div>
          <div>
            <p className="text-white/50 text-xs mb-1">Período</p>
            <p className="text-white font-semibold text-lg">{periodLabels[membership.billing_period]}</p>
          </div>
          <div className="col-span-2">
            <p className="text-white/50 text-xs mb-1">Monto a pagar</p>
            <p className="text-blue-400 font-bold text-2xl">
              ₡{amount.toLocaleString('es-CR')}
            </p>
          </div>
        </div>
      </div>

      {/* Online Payment CTA - Primary */}
      {hasPaymentLink ? (
        <Button
          onClick={handleOnlinePayment}
          className="w-full mb-4 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white gap-2 py-6 text-lg font-semibold shadow-lg shadow-blue-500/25"
        >
          <CreditCard className="w-5 h-5" />
          Pagar en Línea
          <ExternalLink className="w-4 h-4 ml-1" />
        </Button>
      ) : (
        <div className="w-full mb-4 py-4 px-4 bg-white/5 rounded-lg border border-white/10 text-center">
          <p className="text-white/40 text-sm">
            📞 Para planes anuales, contáctanos por WhatsApp o llamada para coordinar tu pago
          </p>
        </div>
      )}

      {/* Secondary contact options */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button
          onClick={handleContactWhatsApp}
          variant="outline"
          className="flex-1 border-green-500/50 text-green-400 hover:bg-green-500/10 gap-2"
        >
          <MessageCircle className="w-5 h-5" />
          Contactar por WhatsApp
        </Button>
        
        <Button
          onClick={() => window.open('tel:+50663217695', '_self')}
          variant="outline"
          className="flex-1 border-blue-500/50 text-blue-400 hover:bg-blue-500/10 gap-2"
        >
          <Phone className="w-5 h-5" />
          Llamar
        </Button>
      </div>

      {/* Info note */}
      <p className="text-white/40 text-xs text-center mt-4">
        Una vez confirmado tu pago, tu membresía se activará automáticamente y podrás disfrutar de todos tus beneficios.
      </p>
    </motion.div>
  );
};

export default PendingActivationView;
